// PersonnelManagementWrapper.tsx — Rebuilt from scratch to match OrganizationForm drawer pattern
import React, { useState } from 'react';
import './companyManagement/CompanyManagement.css';
import './companyManagement/CompanyFormOverrides.css';
import EnhancedPersonnelCreation from './docAI/DocumentProcessingComponents';

// ─── Props ──────────────────────────────────────────────────────────
interface PersonnelManagementWrapperProps {
  getAccessToken: () => Promise<string>;
  onPersonnelListUpdate?: () => void;
  onPersonnelCreated?: (personnelData: any) => void;
  onStepChange?: (step: 'upload' | 'processing' | 'form' | 'saving') => void;
}

// ─── Help Panel Content ─────────────────────────────────────────────
interface HelpItem {
  title: string;
  description: string;
  tip?: string;
}

const HELP_CONTENT: Record<string, HelpItem> = {
  upload: {
    title: 'Upload Documents',
    description:
      'Upload identity documents for the new visitor. The system uses AI to automatically extract personal information from passports, visas, and national IDs — saving you from manual data entry.',
    tip: 'Upload at least one document to proceed. Supported formats: PNG, JPG, PDF (max 10 MB each).',
  },
  processing: {
    title: 'AI Processing',
    description:
      'The system is analysing the uploaded documents using Azure Document Intelligence to extract personal details such as name, nationality, passport number, and dates.',
    tip: 'This usually takes a few seconds. Do not close the window while processing is in progress.',
  },
  form: {
    title: 'Review Extracted Data',
    description:
      'The AI has populated the form with data extracted from the uploaded documents. Please review each field carefully and correct any inaccuracies before saving.',
    tip: 'Fields highlighted in orange were auto-extracted. You can edit any field manually.',
  },
  saving: {
    title: 'Saving Record',
    description:
      'The visitor record is being created and documents are being uploaded to secure storage. This may take a moment.',
    tip: 'Please wait — do not close the window until saving is complete.',
  },
};

// ─── Component ──────────────────────────────────────────────────────
export const PersonnelManagementWrapper: React.FC<PersonnelManagementWrapperProps> = ({
  getAccessToken,
  onPersonnelListUpdate,
  onPersonnelCreated,
  onStepChange,
}) => {
  const [currentStep, setCurrentStep] = useState<'upload' | 'processing' | 'form' | 'saving'>('upload');
  const [showHelp, setShowHelp] = useState<boolean>(true);

  // ─── Callbacks ────────────────────────────────────────────────────
  const handleStatusUpdate = (status: { type: string; message: string }) => {
    console.log(`[PersonnelManagement] ${status.type}: ${status.message}`);
  };

  const handleStepChange = (step: 'upload' | 'processing' | 'form' | 'saving') => {
    setCurrentStep(step);
    onStepChange?.(step);
  };

  const handlePersonnelCreated = (personnelData: any) => {
    console.log('✅ Visitor created:', personnelData);
    onPersonnelCreated?.(personnelData);
    onPersonnelListUpdate?.();
  };

  const handleClose = () => {
    onPersonnelListUpdate?.();
  };

  // ─── Derived state ────────────────────────────────────────────────
  const currentHelp = HELP_CONTENT[currentStep] || HELP_CONTENT.upload;

  // ─── Render ───────────────────────────────────────────────────────
  // DOM tree below is an EXACT copy of OrganizationForm.tsx lines 318-635,
  // with form fields replaced by EnhancedPersonnelCreation.
  return (
    <div className="modal-overlay">
      <div className={`modal-container ${showHelp ? 'company-form-with-help' : ''}`}>
        <div className="modal-header">
          <h2 className="modal-title">
            Create Personnel
            {currentStep !== 'upload' && (
              <span style={{ fontSize: '13px', fontWeight: 400, color: '#9ca3af', marginLeft: '10px' }}>
                {currentStep === 'processing'
                  ? '— Processing...'
                  : currentStep === 'form'
                  ? '— Review & Edit'
                  : currentStep === 'saving'
                  ? '— Saving...'
                  : ''}
              </span>
            )}
          </h2>
          <button
            type="button"
            className="modal-close-btn"
            onClick={handleClose}
            aria-label="Close modal"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="modal-content-with-help">
          {/* ─── Left: Form (same slot as OrganizationForm's <form>) ─── */}
          <div className="modal-form company-form-main">
            <EnhancedPersonnelCreation
              onPersonnelCreated={handlePersonnelCreated}
              onCancel={handleClose}
              getAccessToken={getAccessToken}
              onStatusUpdate={handleStatusUpdate}
              onStepChange={handleStepChange}
            />
          </div>

          {/* ─── Help Panel — always rendered, class toggles width ──── */}
          <div className={`company-help-panel ${showHelp ? 'help-panel-open' : 'help-panel-closed'}`}>
            <div className="help-panel-inner">
              {/* Step indicator */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                marginBottom: '16px',
                fontSize: '11px',
                fontWeight: 600,
                color: '#9ca3af',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                fontFamily: "'Segoe UI', sans-serif",
              }}>
                {(['upload', 'processing', 'form', 'saving'] as const).map((s, i) => (
                  <React.Fragment key={s}>
                    {i > 0 && <span style={{ color: '#d1d5db' }}>›</span>}
                    <span
                      style={{
                        color: currentStep === s ? '#F3694A' : '#d1d5db',
                        fontWeight: currentStep === s ? 700 : 600,
                      }}
                    >
                      {s === 'upload' ? 'Upload' : s === 'processing' ? 'Process' : s === 'form' ? 'Review' : 'Save'}
                    </span>
                  </React.Fragment>
                ))}
              </div>

              <div className={`help-panel-card ${currentStep !== 'upload' ? 'help-active' : ''}`}>
                <h4 className="help-card-title">{currentHelp.title}</h4>
                <p className="help-card-description">{currentHelp.description}</p>
                {currentHelp.tip && (
                  <div className="help-card-tip">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#F3694A"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ flexShrink: 0, marginTop: '1px' }}
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                    <span>{currentHelp.tip}</span>
                  </div>
                )}
              </div>

              {/* Quick reference — always visible */}
              <div className="help-panel-quickref">
                <h4 className="help-quickref-title">Quick Reference</h4>
                <ul className="help-quickref-list">
                  <li>
                    <span className="help-quickref-bullet"></span>
                    <span>Passport and Visa/ID documents are processed by AI</span>
                  </li>
                  <li>
                    <span className="help-quickref-bullet"></span>
                    <span>Profile photo is stored separately — not processed by AI</span>
                  </li>
                  <li>
                    <span className="help-quickref-bullet"></span>
                    <span>All extracted fields can be edited before saving</span>
                  </li>
                  <li>
                    <span className="help-quickref-bullet"></span>
                    <span>Documents are securely stored in Azure Blob Storage</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* ─── Toggle strip — always visible, outer (right) edge ──── */}
          <div className="help-toggle-strip">
            <button
              type="button"
              className="help-toggle-icon-btn"
              onClick={() => setShowHelp(!showHelp)}
              title={showHelp ? 'Hide Guide' : 'Show Guide'}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </button>
            <button
              type="button"
              className="help-toggle-tab"
              onClick={() => setShowHelp(!showHelp)}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                {showHelp ? (
                  <polyline points="9 18 15 12 9 6" />
                ) : (
                  <polyline points="15 18 9 12 15 6" />
                )}
              </svg>
              <span>{showHelp ? 'Hide Guide' : 'Show Guide'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonnelManagementWrapper;